import {useCallback, useMemo} from 'react';

import {assert} from 'Asserts';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {FlowchartEditorModeContext} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/FlowchartEditorModeContext';
import computeTaskDefinitionVersionUpdateChanges from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/computeTaskDefinitionVersionUpdateChanges';
import {TaskSchemaNodeSectionType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  TaskSchemaNode,
  TaskSchemaNodeRowData,
  isInteractiveTaskNode,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {useAllSchemasById} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useAllSchemasById';
import {
  FieldsKey,
  ProcessTaskSchema,
  updateProcessTaskSchemaForTaskDefinitionVersion,
} from 'processes/settings/ProcessTaskSchemaUtils';
import {FieldTableQuery, FieldTableQueryVariables} from 'registry/field/__generated/FieldTableQuery';
import {FIELD_TABLE_QUERY, getFieldTableQueryVariables} from 'registry/field/FieldTable';
import {getFieldOptions} from 'registry/field/FieldTableOptionGetters';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';

import {NodeDetailsSidepanelContext} from '../NodeDetailsSidepanelContext';
import {cacheUpdatedTaskSchemaAndUpdateNode} from '../nodeUtils';
import {getTaskSchemaById} from './transitionFieldInfoUtils';
import type {TransitionFieldInfoSectionProps} from './TransitionMappingsTable';

export function useFieldEditor(
  props: TransitionFieldInfoSectionProps,
  selectedField: TaskSchemaNodeRowData
): {
  schemaField: SchemaField;
  availableFieldOptions: Array<SchemaFieldConstants.FieldOption>;
  schemaFieldSelectors: Array<SchemaFieldSelector>;
  handleFieldUpdate: (updatedField: SchemaField) => Promise<void>;
  taskDefinitionId: string | undefined;
} {
  const context = NodeDetailsSidepanelContext.useContext();
  const editorMode = FlowchartEditorModeContext.useContext();

  const isMapFromStart = selectedField.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START;

  // ProcessTaskSchemaWithoutFlowchartConfigGraphQL is missing relevantForms and flowchartConfig
  // which are required by the ProcessTaskSchema type used by the save utilities.
  const taskSchemaId = isMapFromStart ? props.flowchartTaskSchema.id : props.node.data.taskSchemaId;
  const baseTaskSchema = getTaskSchemaById(context.allSchemasById, taskSchemaId);
  assert(baseTaskSchema != null, 'Expected to find task schema by id in context', {taskSchemaId});
  const taskSchema: ProcessTaskSchema = useMemo(
    () => ({...baseTaskSchema, relevantForms: [], flowchartConfig: null}),
    [baseTaskSchema]
  );

  // fieldsKey determines which array to patch when saving ('taskFields' for inputs, 'outputFields'
  // for outputs). MAP_FROM_START fields are inputs on the flowchart root schema.
  const fieldsKey: FieldsKey =
    selectedField.sectionType === TaskSchemaNodeSectionType.OUTPUTS ? 'outputFields' : 'taskFields';
  const fieldsSource = fieldsKey === 'outputFields' ? taskSchema.outputSchema : taskSchema;
  assert(fieldsSource != null, 'Expected fieldsSource to be non-null', {fieldsKey, taskSchemaId});
  const allFieldsForSection: Array<SchemaField> = useMemo(() => fieldsSource.allFields, [fieldsSource]);

  const schemaField = useMemo(() => {
    const field = allFieldsForSection.find((f) => f.apiId === selectedField.clientId);
    assert(field != null, 'Expected to find schemaField by clientId', {clientId: selectedField.clientId});
    return field;
  }, [allFieldsForSection, selectedField.clientId]);

  const isFlowchartTaskSchema = props.flowchartTaskSchema.id === taskSchemaId;
  const taskDefinition = context.taskDefinitionsForSidepanel.find(
    (def) => def.latestVersion.processTaskSchema.id === taskSchemaId
  );
  const taskDefinitionId =
    isMapFromStart || isFlowchartTaskSchema ? context.flowchartTaskDefinitionId : taskDefinition?.id;

  const {storableSchemas} = useAllSchemasById(props.flowchartTaskSchema);
  const canEditDefinition = !props.locked && taskDefinitionId != null;
  const fieldTableQueryResult = useSkippableBenchlingQuery<FieldTableQuery, FieldTableQueryVariables>(
    FIELD_TABLE_QUERY,
    {variables: getFieldTableQueryVariables(taskSchema.organization.id), skip: !canEditDefinition}
  );

  const availableFieldOptions = useMemo(() => {
    if (fieldTableQueryResult.type !== QueryResultType.SUCCESS) {
      return [];
    }
    return getFieldOptions({
      schemaFieldSelectors: fieldTableQueryResult.data.schemaFieldSelectors,
      // StorableSchemaGQL is structurally compatible with StorableSchema for field options
      storableSchemas: (storableSchemas ?? []) as unknown as Array<StorableSchema>,
      tagSchemas: [],
      schemaInterfaces: fieldTableQueryResult.data.schemaInterfaces,
      initialField: schemaField,
      includeSnapshotField: true,
      snapshotFieldDisabledMessage:
        'Snapshot fields cannot be changed here. Please click on the node to edit this field.',
      includeBooleanField: true,
    });
  }, [fieldTableQueryResult, schemaField, storableSchemas]);

  const schemaFieldSelectors: Array<SchemaFieldSelector> =
    fieldTableQueryResult.type === QueryResultType.SUCCESS
      ? fieldTableQueryResult.data.schemaFieldSelectors
      : [];

  const handleFieldUpdate = useCallback(
    async (updatedField: SchemaField): Promise<void> => {
      assert(taskDefinitionId != null, 'Expected taskDefinitionId to be defined when saving a field edit');
      const updatedFields = allFieldsForSection.map((f) =>
        f.apiId === selectedField.clientId ? updatedField : f
      );
      const taskDefinitionVersion = await updateProcessTaskSchemaForTaskDefinitionVersion(
        {[fieldsKey]: updatedFields},
        taskDefinitionId,
        () => {},
        () => {},
        taskSchema,
        taskSchema.outputSchema != null
      );
      if (taskDefinitionVersion != null && !isInaccessible(taskDefinitionVersion.processTaskSchema)) {
        if (isMapFromStart) {
          const rootNode = props.flowchart.nodes.find(
            (n): n is TaskSchemaNode =>
              isInteractiveTaskNode(n) && n.data.taskSchemaId === props.flowchartTaskSchema.id
          );
          assert(rootNode != null, 'Expected to find root node to update after saving MAP_FROM_START field', {
            taskSchemaId: props.flowchartTaskSchema.id,
          });
          // onUpdateTaskDefinitionVersion closes over the sidepanel's selectedNode. For
          // MAP_FROM_START saves we want to target the root node, so we need to make our own update
          // function
          const updateNodeFn: typeof context.onUpdateTaskDefinitionVersion = (updater, isBlocking) =>
            context.applyFlowchartChanges(
              (flowchartDraftState) =>
                computeTaskDefinitionVersionUpdateChanges(
                  flowchartDraftState,
                  rootNode.id,
                  updater,
                  false,
                  editorMode
                ),
              isBlocking
            );
          cacheUpdatedTaskSchemaAndUpdateNode(
            taskDefinitionVersion.processTaskSchema,
            rootNode,
            updateNodeFn,
            taskDefinitionVersion
          );
        } else {
          cacheUpdatedTaskSchemaAndUpdateNode(
            taskDefinitionVersion.processTaskSchema,
            props.node,
            context.onUpdateTaskDefinitionVersion,
            taskDefinitionVersion
          );
        }
      }
    },
    [
      allFieldsForSection,
      context,
      fieldsKey,
      isMapFromStart,
      props.flowchart.nodes,
      props.flowchartTaskSchema.id,
      props.node,
      selectedField.clientId,
      taskDefinitionId,
      taskSchema,
      editorMode,
    ]
  );

  return {schemaField, availableFieldOptions, schemaFieldSelectors, handleFieldUpdate, taskDefinitionId};
}
